import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Plus, Sparkles, ArrowRightLeft, Share2, LogOut, LogIn } from "lucide-react";
import { useTrip, useUI } from "@/lib/store";
import { TRIP_DATES, fmtLong, fmtTime, hotelForNight } from "@/lib/dates";
import { DateStrip } from "@/components/DateStrip";
import { EventCard, shareText } from "@/components/EventCard";
import { TransportCard } from "@/components/TransportCard";
import { HotelCard } from "@/components/HotelCard";
import { CalendarButton } from "@/components/CalendarButton";
import { useNavigate } from "react-router-dom";

export default function Trip() {
  const { trip, today, organiser, profile } = useTrip();
  const { openEditor, showDriver } = useUI();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [mineOnly, setMineOnly] = useState(false);
  const initial = params.get("date") || (params.get("city") && trip.destinations.find((d) => d.id === params.get("city"))?.dates[0]) || (TRIP_DATES.includes(today) ? today : TRIP_DATES[0]);
  const [date, setDate] = useState(initial);
  useEffect(() => { const d = params.get("date"); if (d && d !== date) setDate(d); }, [params]); // eslint-disable-line
  const select = (d) => { setDate(d); setParams({ date: d }, { replace: true }); };

  const day = trip.days.find((d) => d.date === date);
  const dest = trip.destinations.find((d) => d.id === day?.cityId);
  const transports = trip.transports.filter((t) => t.date === date);
  const events = useMemo(() => trip.events.filter((e) => e.date === date && (!e.private || organiser) && (!mineOnly || e.ownerId === profile?.id)), [trip.events, date, organiser, mineOnly, profile]);
  const checkIn = trip.hotels.find((h) => h.checkIn === date);
  const checkOut = trip.hotels.find((h) => h.checkOut === date);
  const tonight = hotelForNight(trip.hotels, date);
  const ideasHere = trip.ideas.filter((i) => i.cityId === day?.cityId);
  const idx = TRIP_DATES.indexOf(date);

  const touch = useRef(null);
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0]; const dx = t.clientX - touch.current.x; const dy = t.clientY - touch.current.y; touch.current = null;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 2) return;
    if (dx < 0 && idx < 9) select(TRIP_DATES[idx + 1]); else if (dx > 0 && idx > 0) select(TRIP_DATES[idx - 1]);
  };

  return (
    <div className="pb-8">
      <div className="sticky top-0 z-30 -mx-4 bg-[var(--parchment)]/95 px-4 pt-3 backdrop-blur-lg md:static md:mx-0 md:px-0">
        <p className="eyebrow">Trip timeline</p>
        <DateStrip value={date} onChange={select} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={date} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="pan-y mt-3 space-y-4" data-testid={`day-view-${date}`}>
          <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 150 }}>
            <img src={dest?.image} alt={day?.city} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2620]/85 to-[#0F2620]/30" />
            <div className="relative p-5 text-[var(--parchment)]">
              <p className="eyebrow !text-[var(--brass)]">{day?.transition ? `${day.transition.from} → ${day.transition.to}` : `${day?.city} · ${dest?.chinese}`}</p>
              <h1 data-testid="day-title" className="font-display mt-1 text-3xl font-semibold leading-tight">{fmtLong(date)}</h1>
              <p className="mt-1 text-sm text-[var(--parchment)]/85">{day?.title}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <CalendarButton kind="day" item={date} label="Add this day" compact testId="calendar-day-btn" />
                <button data-testid="share-day-btn" onClick={() => shareText(fmtLong(date), `${fmtLong(date)} · ${day?.city}\n${[...transports.map((t) => `${t.title}${t.departTime ? ` ${fmtTime(t.departTime)}` : " (time TBC)"}`), ...events.map((e) => `${e.startTime ? fmtTime(e.startTime) + " " : ""}${e.title}`)].join("\n")}${tonight ? `\nHotel: ${tonight.name} — ${tonight.addressChinese}` : ""}`)} className="tap inline-flex items-center gap-1.5 rounded-full border border-white/40 px-3 py-1.5 text-xs font-semibold"><Share2 className="h-3.5 w-3.5" /> Share</button>
              </div>
            </div>
          </div>

          {day?.transition && (
            <p data-testid="transition-alert" className="flex items-center gap-2 rounded-2xl bg-[var(--coral-soft)] px-4 py-3 text-sm font-medium text-[#8a3f26]"><ArrowRightLeft className="h-4 w-4" /> Travel day: {day.transition.from} → {day.transition.to}. {checkOut ? `Check out of ${checkOut.name}.` : ""}</p>
          )}

          {checkOut && <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><LogOut className="h-4 w-4" /> Check out · {checkOut.name}</div>}
          {transports.map((t) => <TransportCard key={t.id} t={t} />)}

          {checkIn && (
            <div className="card-soft p-4">
              <div className="flex items-start justify-between gap-3">
                <div><p className="eyebrow flex items-center gap-1"><LogIn className="h-3 w-3" /> Check in</p><h3 className="font-display text-2xl font-semibold">{checkIn.name}</h3><p className="text-sm text-[var(--ink-muted)]">{checkIn.nights} nights · {checkIn.roomSummary}</p></div>
              </div>
              <p className="font-cn mt-2 text-lg font-semibold text-[var(--jade-deep)]">{checkIn.addressChinese}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`day-show-driver-${checkIn.id}`} onClick={() => showDriver(checkIn)} className="tap rounded-full bg-[var(--jade)] px-4 py-2 text-sm font-semibold text-[var(--parchment)]">Show driver</button>
                <button data-testid="day-open-stays" onClick={() => navigate("/more/stays")} className="tap rounded-full border bg-white px-4 py-2 text-sm font-semibold">Hotel details</button>
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="eyebrow">{events.length ? "Plans" : "Nothing scheduled yet"}</p>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-full border bg-white p-0.5 text-[11px] font-semibold">{[[false, "All"], [true, "Mine"]].map(([v, l]) => <button key={l} data-testid={`trip-filter-${l.toLowerCase()}`} onClick={() => setMineOnly(v)} className={`tap rounded-full px-3 py-1.5 ${mineOnly === v ? "bg-[var(--jade)] text-[var(--parchment)]" : ""}`}>{l}</button>)}</div>
                <button data-testid="add-event-btn" onClick={() => openEditor({ mode: "add", date })} className="tap inline-flex min-h-[32px] items-center gap-1 rounded-full bg-[var(--plum)] px-3 py-1.5 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Add</button>
              </div>
            </div>
            <div className="space-y-3">
              {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
              {!events.length && (
                <div data-testid="open-day-card" className="rounded-3xl border border-dashed p-5 text-center">
                  <p className="font-display text-xl font-semibold">{day?.openDay ? "An open day" : "No plans entered"}</p>
                  <p className="mt-1 text-sm text-[var(--ink-muted)]">{day?.date === "2026-10-29" ? "Flight home — details to be confirmed." : `Nothing is booked for ${day?.city} yet. Pick something from the ideas below.`}</p>
                </div>
              )}
            </div>
          </div>

          {ideasHere.length > 0 && day?.date !== "2026-10-29" && (
            <div>
              <p className="eyebrow mb-2 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Ideas for {day?.city}</p>
              <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0">
                {ideasHere.map((i) => (
                  <button key={i.id} data-testid={`day-idea-${i.id}`} onClick={() => openEditor({ mode: "add", date, idea: i })} className="tap shrink-0 rounded-2xl border bg-amber-50/70 px-3 py-2 text-left text-sm">
                    <span className="font-semibold">{i.title}</span>{i.flag && <span className="block text-[10px] text-amber-800">{i.flag}</span>}
                    <span className="block text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">Tap to add</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tonight && !checkIn && <HotelCard hotel={tonight} mode="tonight" />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
